/**
 * Step 6 data layer for the client brief, Team → Client briefs and Clients → Engagement (ADR-001 §11.8).
 * Every shape is the contract's (packages/contracts brief.ts, engagement.ts); nothing here computes a state —
 * the cell's colour, label and sub-label come from the server.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  ActivityKey,
  AddEngagementTasksBody,
  EngagementGridQuery,
  EngagementState,
  ListBriefsQuery,
  LogEngagementBody,
  UpdateEngagementActivityBody,
} from '@amplispot/contracts';
import { call, newIdempotencyKey } from '../api';
import { wkeys } from './work';

export const ekeys = {
  all: ['eng'] as const,
  grid: (q: EngagementGridQuery) => ['eng', 'grid', q] as const,
  client: (id: string) => ['eng', 'client', id] as const,
  activities: ['eng', 'activities'] as const,
  brief: (id: string) => ['eng', 'brief', id] as const,
  briefs: (q: ListBriefsQuery) => ['eng', 'briefs', q] as const,
};

/** A write to a cell changes the grid, the brief, the briefs list, the client's tasks and the hub badge. */
export function refreshEngagement(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ekeys.all });
  void qc.invalidateQueries({ queryKey: wkeys.all });
  void qc.invalidateQueries({ queryKey: ['me'] });
  void qc.invalidateQueries({ queryKey: ['clients'] });
}

/** Cell colour, as the prototype's `engState`: overdue red, due soon amber, on track green. */
export const STATE_CLS: Record<EngagementState, 'cold' | 'warm' | 'hot'> = { overdue: 'cold', due_soon: 'warm', on_track: 'hot' };

/** The row's "N of 8" chip: 3+ overdue red, any overdue amber, else green (prototype engage, briefs). */
export const overdueTone = (overdue: number): 'cold' | 'warm' | 'hot' => (overdue >= 3 ? 'cold' : overdue ? 'warm' : 'hot');

export function useEngagementGrid(q: EngagementGridQuery) {
  return useQuery({ queryKey: ekeys.grid(q), queryFn: () => call('engagement.grid', { query: q }) });
}
export function useEngagementActivities(enabled = true) {
  return useQuery({ queryKey: ekeys.activities, queryFn: () => call('engagementActivities.list'), enabled });
}
export function useBrief(id: string | null) {
  return useQuery({ queryKey: ekeys.brief(id ?? ''), queryFn: () => call('brief.get', { params: { id: id! } }), enabled: !!id });
}
export function useBriefs(q: ListBriefsQuery, enabled = true) {
  return useQuery({ queryKey: ekeys.briefs(q), queryFn: () => call('briefs.list', { query: q }), enabled });
}

export function useLogEngagement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; key: ActivityKey; body: LogEngagementBody }) =>
      call('engagement.log', { params: { id: v.clientId, activity_key: v.key }, body: v.body, idempotencyKey: newIdempotencyKey() }),
    onSuccess: () => refreshEngagement(qc),
  });
}
export function useBookEngagement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { clientId: string; key: ActivityKey; booked_for: string | null }) =>
      call('engagement.book', { params: { id: v.clientId, activity_key: v.key }, body: { booked_for: v.booked_for } }),
    onSuccess: () => refreshEngagement(qc),
  });
}
/** "Add what is due to the task list" for one client (the API is per client; the grid button loops the rows). */
export function addEngagementTasks(clientId: string, body: AddEngagementTasksBody = {}) {
  return call('engagement.addTasks', { params: { id: clientId }, body, idempotencyKey: newIdempotencyKey() });
}
export function useUpdateActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { key: ActivityKey; body: UpdateEngagementActivityBody }) => call('engagementActivities.update', { params: { key: v.key }, body: v.body }),
    onSuccess: () => {
      refreshEngagement(qc);
      // A cadence save bumps the one shared Settings config_version (api routes/engagement.ts), so the cached
      // thresholds/flags/working-day versions are stale now — without this, the next Settings save is a false 412.
      void qc.invalidateQueries({ queryKey: ['settings'] });
    },
  });
}

/** A wa.me link for a phone the server chose to show (UserRef.phone), or null when there is none to use. */
export function waLink(phone: string | null | undefined, text: string): string | null {
  const d = (phone ?? '').replace(/\D/g, '');
  return d.length >= 8 ? `https://wa.me/${d}?text=${encodeURIComponent(text)}` : null;
}
